/**
 * PhraseMaker — generates styled SVG text in the game's signature visual style.
 *
 * Two layers: cream (#f7e5c5) foreground + charcoal (#262626) shadow, offset left+down.
 * Both layers rotated counterclockwise by 7.54° (right side higher).
 * Font: Barlow Condensed ExtraBold (italic).
 *
 * Shadow offset scales with font size. At 62pt baseline: ~3.5px left, ~2.5px down.
 */

import React from 'react';

// ─── Constants ─────────────────────────────────────────

const CREAM = '#f7e5c5';
const CHARCOAL = '#262626';
const SLOPE_DEG = -7.54;            // counterclockwise (right side higher)
const FONT_FAMILY = "'Barlow Condensed', sans-serif";
const FONT_WEIGHT = 800;            // ExtraBold
const FONT_STYLE = 'italic';

/** Shadow offset at 62pt reference size — reduced and equalized */
const REF_FONT_SIZE = 62;
const REF_SHADOW_DX = -2.0;         // left
const REF_SHADOW_DY = 2.0;          // down

// ─── Font loader ───────────────────────────────────────

let fontLoadPromise: Promise<void> | null = null;

/** Ensure Barlow Condensed ExtraBold Italic is loaded */
export function ensureBarlowReady(): Promise<void> {
  if (fontLoadPromise) return fontLoadPromise;

  // Check if already available
  if (document.fonts.check(`${FONT_STYLE} ${FONT_WEIGHT} 16px ${FONT_FAMILY}`)) {
    fontLoadPromise = Promise.resolve();
    return fontLoadPromise;
  }

  // Load from Google Fonts
  fontLoadPromise = new Promise<void>((resolve) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Barlow+Condensed:ital,wght@1,800&display=swap';
    link.onload = () => {
      document.fonts.ready.then(() => resolve());
    };
    link.onerror = () => resolve(); // degrade gracefully
    document.head.appendChild(link);
  });

  return fontLoadPromise;
}

// ─── Core SVG component ───────────────────────────────

export interface PhraseProps {
  /** The text to render (use \n for line breaks) */
  text: string;
  /** Font size in px */
  fontSize: number;
  /** Optional fixed width — if omitted, uses Canvas measureText for auto-sizing */
  width?: number;
  /** Optional fixed height — if omitted, derived from fontSize and line count */
  height?: number;
  /** Optional className on the root SVG */
  className?: string;
  /** Optional inline style on the root SVG */
  style?: React.CSSProperties;
  /** Text anchor — default 'start' */
  textAnchor?: 'start' | 'middle' | 'end';
  /** Foreground fill color — default cream (#f7e5c5) */
  fillColor?: string;
  /** Shadow fill color — default charcoal (#262626) */
  shadowColor?: string;
  /** Line height multiplier for multi-line text — default 1.0 (use 0.75 for tight spacing) */
  lineHeight?: number;
}

/**
 * Renders a styled phrase as SVG with dual-layer shadow effect.
 *
 * The SVG auto-sizes to fit the text (with padding for rotation and shadow).
 */
const Phrase: React.FC<PhraseProps> = ({
  text,
  fontSize,
  width: fixedWidth,
  height: fixedHeight,
  className,
  style,
  textAnchor = 'start',
  fillColor,
  shadowColor,
  lineHeight = 1.0,
}) => {
  const fill = fillColor ?? CREAM;
  const shadow = shadowColor ?? CHARCOAL;
  // Scale shadow offset proportionally to font size
  const sizeScale = fontSize / REF_FONT_SIZE;
  const shadowDx = REF_SHADOW_DX * sizeScale;
  const shadowDy = REF_SHADOW_DY * sizeScale;

  const lines = text.split('\n');
  const lineCount = lines.length;
  const lineStep = fontSize * lineHeight;  // vertical distance between baselines

  // Measure widest line via canvas for auto-sizing
  const measuredWidth = React.useMemo(() => {
    if (fixedWidth !== undefined) return fixedWidth;
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.font = `${FONT_STYLE} ${FONT_WEIGHT} ${fontSize}px ${FONT_FAMILY}`;
        return Math.ceil(Math.max(...lines.map(l => ctx.measureText(l).width)));
      }
    } catch { /* fallback */ }
    const longestLine = lines.reduce((a, b) => a.length > b.length ? a : b, '');
    return Math.ceil(longestLine.length * fontSize * 0.55);
  }, [text, fontSize, fixedWidth, lines]);

  // Padding for rotation overshoot and shadow
  const angleRad = Math.abs(SLOPE_DEG) * (Math.PI / 180);
  const rotPadX = Math.ceil(fontSize * Math.sin(angleRad)) + Math.abs(shadowDx);
  const rotPadY = Math.ceil(measuredWidth * Math.sin(angleRad) * 0.5) + shadowDy;

  const svgW = fixedWidth ?? (measuredWidth + rotPadX * 2 + 4);
  const totalTextH = fontSize + (lineCount - 1) * lineStep;
  const svgH = fixedHeight ?? (totalTextH * 1.2 + rotPadY * 2);

  // Anchor positions
  let textX: number;
  if (textAnchor === 'middle') textX = svgW / 2;
  else if (textAnchor === 'end') textX = svgW - rotPadX;
  else textX = rotPadX + Math.abs(shadowDx);

  // First line baseline — vertically center the text block
  const blockTop = (svgH - totalTextH) / 2 + fontSize * 0.8;

  const rotateStr = `rotate(${SLOPE_DEG}, ${svgW / 2}, ${svgH / 2})`;

  const renderLines = (dx: number, dy: number, color: string) => (
    <text
      fill={color}
      fontFamily={FONT_FAMILY}
      fontWeight={FONT_WEIGHT}
      fontStyle={FONT_STYLE}
      fontSize={fontSize}
      textAnchor={textAnchor}
      dominantBaseline="auto"
      transform={rotateStr}
    >
      {lines.map((line, i) => (
        <tspan key={i} x={textX + dx} y={blockTop + i * lineStep + dy}>
          {line}
        </tspan>
      ))}
    </text>
  );

  return (
    <svg
      width={svgW}
      height={svgH}
      viewBox={`0 0 ${svgW} ${svgH}`}
      className={className}
      style={{ overflow: 'visible', ...style }}
    >
      {renderLines(shadowDx, shadowDy, shadow)}
      {renderLines(0, 0, fill)}
    </svg>
  );
};

export default React.memo(Phrase);
